import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import useCompany from "../../../hooks/useCompany";
import useEvent from "../../../hooks/useEvent";
import { Users, Boxes, Image as ImageIcon, Calendar, Clock, MapPin, Sparkles } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const getLocalISODate = (value) => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function Home() {
  const { activeCompany: company, companyOverview, getCompanyUsers } = useCompany();
  const { events, getAllEvents } = useEvent();

  const [overview, setOverview] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const eventsByDate = useMemo(() => {
    const map = {};
    events?.forEach(event => {
      if (!event.startDate) return;
      const dateKey = getLocalISODate(event.startDate);
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(event);
    });
    return map;
  }, [events]);

  // Handler para detectar hover sobre días con eventos
  useEffect(() => {
    const handleMouseOver = (e) => {
      const dayElement = e.target.closest('[data-day]');
      if (!dayElement) return;

      const hoveredDateKey = dayElement.getAttribute('data-day');

      if (hoveredDateKey && eventsByDate[hoveredDateKey]) {
        setHoveredDay(hoveredDateKey);
        const rect = dayElement.getBoundingClientRect();
        setTooltipPosition({ 
          x: rect.left + rect.width / 2, 
          y: rect.top 
        });
      } else {
        setHoveredDay(null);
      }
    };

    const handleMouseOut = (e) => {
      const fromDay = e.target.closest('[data-day]');
      const toDay = e.relatedTarget?.closest?.('[data-day]');
      if (fromDay && !toDay) {
        setHoveredDay(null);
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [eventsByDate]);

  useEffect(() => {
    const fetchData = async () => {
      if (company && company.id) {
        try {
          const data = await companyOverview(company.id);
          setOverview(data);
        } catch (error) {
          console.error("Error fetching company overview:", error);
        }

        try {
          const users = await getCompanyUsers(company.id);
          setCompanyUsers(users || []);
        } catch (error) {
          console.error("Error fetching company users:", error);
        }

        try {
          await getAllEvents();
        } catch (error) {
          console.error("Error fetching events:", error);
        }
      }
    };

    fetchData();
  }, [company]);

  const latestNFT = overview?.nfts?.length ? overview.nfts[overview.nfts.length - 1] : null;
  const latestRWA = overview?.rwas?.length ? overview.rwas[overview.rwas.length - 1] : null;

  const getNFTImage = (nft) => {
    const img1 = nft?.RWANFTData?.find?.((d) => d.key === "image")?.value;
    const img2 = nft?.rwa_nft_data?.find?.((d) => d.key === "image")?.value;
    const img3 = nft?.rwa?.rwa_data?.find?.((d) => d.key === "image")?.value;
    return img1 || img2 || img3 || "/images/placeholder.png";
  };

  const getRWAImage = (rwa) => {
    const img1 = rwa?.RWAData?.find?.((d) => d.key === "image")?.value;
    const img2 = rwa?.rwa_data?.find?.((d) => d.key === "image")?.value;
    return img1 || img2 || "/images/placeholder.png";
  };

  const totalUsers = companyUsers.length;
  const latestMembers = [...companyUsers].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);

  const formatMonth = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}`;
  };

  // TODOS los meses del año, siempre
  const allMonths = [
    "01", "02", "03", "04", "05", "06",
    "07", "08", "09", "10", "11", "12"
  ];

  const getChartData = (items) => {
    if (!items || !Array.isArray(items)) return [];

    const grouped = {};
    items.forEach(item => {
      if (item?.createdAt) {
        const month = formatMonth(item.createdAt).split("-")[1]; // solo mes
        grouped[month] = (grouped[month] || 0) + 1;
      }
    });

    return allMonths.map(month => ({
      month,
      total: grouped[month] || 0, // solo el valor de ese mes
    }));
  };

  const nftChartData = useMemo(() => {
    return getChartData(overview?.nfts);
  }, [overview]);
  
  const rwaChartData = useMemo(() => {
    return getChartData(overview?.rwas);
  }, [overview]);

  return (
    <div className="2xl:p-6 p-3 grid grid-cols-1 xl:grid-cols-12 2xl:gap-6 gap-4">
      
      {/* STATS */}
      <div className="col-span-12 grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: "Total Users", value: totalUsers, icon: Users },
          { label: "Total RWAs", value: overview?.rwas?.length ?? 0, icon: Boxes },
          { label: "Total NFTs", value: overview?.totalNfts ?? overview?.nfts?.length ?? 0, icon: ImageIcon },
          { label: "Total Events", value: events?.length ?? 0, icon: Calendar },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-gray-100 rounded-2xl p-4 flex items-center gap-4 shadow-sm min-w-0">
              <div className="bg-blue-600 rounded-full w-10 h-10 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-gray-500 text-sm truncate">{stat.label}</p>
                <p className="text-lg font-bold">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* ANALYTICS */}
      <div className="col-span-12 grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4 2xl:gap-6">

        {/* NFT Growth */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 shadow-sm border border-gray-200/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg text-gray-800">NFT Growth</h3>
            <span className="text-xs font-medium text-purple-600 bg-purple-100 px-3 py-1 rounded-full">Per month</span>
          </div>
          {nftChartData.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No data available</p>
          ) : (
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={nftChartData} margin={{ top: 20, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis 
                    dataKey="month" 
                    stroke="#9ca3af"
                    style={{ fontSize: '11px', fontWeight: '500' }}
                    tick={{ dy: 5 }}
                  />
                  <YAxis 
                    stroke="#9ca3af"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                      color: '#fff',
                    }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                    wrapperStyle={{ color: '#fff' }}
                    formatter={(value) => [String(value), 'Total']}
                    cursor={{ fill: 'rgba(168, 85, 247, 0.1)' }}
                  />
                  <Bar 
                    dataKey="total" 
                    fill="url(#colorNFT)"
                    radius={[8, 8, 0, 0]}
                    isAnimationActive={true}
                  />
                  <defs>
                    <linearGradient id="colorNFT" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2461DD" stopOpacity={0.9}/>
                      <stop offset="95%" stopColor="#6D9CE2" stopOpacity={0.4}/>
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Events Calendar */}
        <div className="bg-gray-100 rounded-2xl p-6 shadow-sm relative">
          <h3 className="font-semibold mb-4 text-lg">Events Calendar</h3>

          <div className="flex justify-center">
            <div style={{ transform: 'scale(1.1)', transformOrigin: 'top center', marginBottom: '3rem' }}>
              <DayPicker
                modifiers={{
                  eventDay: Object.keys(eventsByDate).map((dateKey) => {
                    const [year, month, day] = dateKey.split("-").map(Number);
                    return new Date(year, month - 1, day);
                  }),
                }}
                modifiersStyles={{
                  eventDay: {
                    backgroundColor: "#3b82f6",
                    color: "white",
                    borderRadius: "0.5rem",
                    fontWeight: "bold",
                  },
                }}
              />
            </div>
          </div>

          {/* Custom Tooltip */}
          {hoveredDay && eventsByDate[hoveredDay] && (
            <div 
              className="fixed pointer-events-none z-[9999] max-w-sm"
              style={{
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                transform: 'translate(-50%, calc(-100% - 12px))',
              }}
            >
              <div className="bg-gradient-to-br from-white to-gray-200 text-white rounded-xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2.5 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  <span className="font-semibold text-sm">
                    {eventsByDate[hoveredDay].length === 1 
                      ? '1 Event' 
                      : `${eventsByDate[hoveredDay].length} Events`}
                  </span>
                </div>
                
                {/* Events List */}
                <div className="p-3 space-y-3">
                  {eventsByDate[hoveredDay].map((event, idx) => {
                    const time = new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div 
                        key={idx} 
                        className={`${idx > 0 ? 'pt-3 border-t border-gray-200' : ''} space-y-1.5`}
                      >
                        <div className="font-semibold text-sm text-black flex items-start gap-2">
                          <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
                          <span className="leading-tight">{event.name}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-black ml-6">
                          <Clock className="w-3.5 h-3.5 text-blue-400" />
                          <span>{time}</span>
                        </div>
                        
                        {event.location && (
                          <div className="flex items-center gap-2 text-xs text-black ml-6">
                            <MapPin className="w-3.5 h-3.5 text-blue-400" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                        
                        {event.price && parseFloat(event.price) > 0 && (
                          <div className="flex items-center gap-2 text-xs ml-6">
                            <span className="bg-green-500/20 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              {event.price} {event.currency || ''}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Arrow */}
              <div 
                className="absolute left-1/2 -translate-x-1/2 top-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #d2d2d2ff',
                }}
              />
            </div>
          )}
        </div>

        {/* RWA Growth */}
        <div className="xl:col-span-2 2xl:col-span-1 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 shadow-sm border border-gray-200/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg text-gray-800">RWA Growth</h3>
            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-3 py-1 rounded-full">Per month</span>
          </div>
          {rwaChartData.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No data available</p>
          ) : (
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rwaChartData} margin={{ top: 20, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis 
                    dataKey="month" 
                    stroke="#9ca3af"
                    style={{ fontSize: '11px', fontWeight: '500' }}
                    tick={{ dy: 5 }}
                  />
                  <YAxis 
                    stroke="#9ca3af"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                      color: '#fff',
                    }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                    wrapperStyle={{ color: '#fff' }}
                    formatter={(value) => [String(value), 'Total']}
                    cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                  />
                  <Bar 
                    dataKey="total" 
                    fill="url(#colorRWA)"
                    radius={[8, 8, 0, 0]}
                    isAnimationActive={true}
                  />
                  <defs>
                    <linearGradient id="colorRWA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2B36D9" stopOpacity={0.9}/>
                      <stop offset="95%" stopColor="#A1A1A1" stopOpacity={0.4}/>
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* CARDS */}
      <div className="col-span-12 grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4 2xl:gap-6">
        {/* Latest NFT */}
        <div className="bg-gray-100 rounded-2xl p-4 shadow-sm min-w-0">
          <div className="flex justify-between items-center mb-4 gap-2">
            <span className="font-semibold truncate">Latest NFT</span>
            <Link className="text-sm px-4 py-2 rounded-2xl bg-white font-semibold shrink-0" to={"/dashboard/rwa"}>See all</Link>
          </div>
          {latestNFT ? (
            <div className="flex gap-2 items-center min-w-0">
              <img src={getNFTImage(latestNFT)} alt="NFT" className="w-10 h-10 rounded-lg object-cover shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{latestNFT.rwa.name}</p>
                <p className="text-xs text-gray-500 truncate">{new Date(latestNFT.createdAt).toLocaleString()}</p>
              </div>
            </div>
          ) : (<p className="text-gray-400">No NFTs</p>)}
        </div>

        {/* Latest Product */}
        <div className="bg-gray-100 rounded-2xl p-4 shadow-sm min-w-0">
          <div className="flex justify-between items-center mb-4 gap-2">
            <span className="font-semibold truncate">Latest Product</span>
            <Link className="text-sm px-4 py-2 rounded-2xl bg-white font-semibold shrink-0" to={"/dashboard/rwa"}>See all</Link>
          </div>
          {latestRWA ? (
            <div className="flex gap-2 items-center min-w-0">
              <img src={getRWAImage(latestRWA)} alt="RWA" className="w-10 h-10 rounded-lg object-cover shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{latestRWA.name}</p>
                <p className="text-xs text-gray-500 truncate">{new Date(latestRWA.createdAt).toLocaleString()}</p>
              </div>
            </div>
          ) : (<p className="text-gray-400">No Products</p>)}
        </div>

        {/* Latest Members */}
        <div className="xl:col-span-2 2xl:col-span-1 bg-gray-100 rounded-2xl p-4 shadow-sm min-w-0">
          <div className="flex justify-between items-center mb-4 gap-2">
            <span className="font-semibold truncate">Latest Members</span>
            <Link className="text-sm px-4 py-2 rounded-2xl bg-white font-semibold shrink-0" to={"/dashboard/members"}>See all</Link>
          </div>
          {latestMembers.length === 0 ? (
            <p className="text-gray-400">No members</p>
          ) : (
            latestMembers.map((user, i) => (
              <div key={i} className="flex gap-2 items-center mb-3 min-w-0">
                <img src={`https://api.dicebear.com/9.x/initials/svg?backgroundColor=2B36D9&seed=${user?.displayName || user?.name || "User"}`} alt="User Avatar" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{user?.displayName || user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default Home;